import Tooltip from '@/components/Tooltip';
import useGlossaryTerms from '@/hooks/useGlossaryTerms';
import { toPercentage, toTruncated } from '@/utilities/formatting';
import { useOverviewTranslation } from '@/pages/CompanyReport/components/OverviewSection/hooks/useOverviewTranslation';
import { format } from 'date-fns';
import type { useCompanyStock } from '../hooks/useCompanyStock';

import styled from 'styled-components';
import { Link } from '@simplywallst/ui-core/next';

// TODO: Convert to tailwind (unsure why conversion breaks the tooltip)
const Term = styled('span')`
  white-space: nowrap;
  color: inherit;
  border-bottom: 1px dotted ${({ theme }) => theme.color.darkerGray};
  cursor: help;
  display: inline-block;
  &:hover {
    color: ${({ theme }) => theme.color.white};
  }
`;

const TooltipTitle = ({ children }: { children: React.ReactNode }) => (
  <div className="max-w-[200px] text-tiny">{children}</div>
);

export const CompanyDetails = ({
  canonicalURL,
  details,
}: {
  canonicalURL: string;
  details: ReturnType<typeof useCompanyStock>['details'];
}) => {
  const term = useGlossaryTerms();
  const t = useOverviewTranslation();

  return (
    <div className="@container">
      <div className="grid items-start gap-x-10 gap-y-3 @[100ch]:grid-cols-[auto_minmax(0,1fr)]">
        <div className="grid gap-x-6 gap-y-3 @[40ch]:grid-cols-[auto_minmax(0,1fr)] @[100ch]:grid-cols-1">
          <div>
            <div className="text-tiny text-soft">Last Reported Earnings </div>
            <div className="text-sm">
              {details.lastEarningsUpdate
                ? format(details.lastEarningsUpdate, 'MMM dd, yyyy')
                : t('fundamentals.income.details.notAvailable')}
            </div>
          </div>
          <div>
            <div className="text-tiny text-soft">Yearly revenue growth</div>
            <div className="text-sm">
              {details.revenueGrowthAnnual
                ? toPercentage(details.revenueGrowthAnnual, 2)
                : t('fundamentals.income.details.notAvailable')}
            </div>
          </div>
        </div>
        <dl className="m-0 -mx-2 max-w-[45ch] text-xs [&>div:nth-child(odd)]:bg-surface-2 [&>div]:rounded-md [&>div]:px-2 [&>div]:py-1">
          <div className="flex justify-between py-1 align-bottom">
            <dt className="text-soft">
              <Tooltip
                title={
                  <TooltipTitle>{term('earnings per share')}</TooltipTitle>
                }
                light
                openDelay={300}
              >
                <Term>{t('fundamentals.income.details.earningsPerShare')}</Term>
              </Tooltip>
            </dt>
            <dd className="text-right">
              {toTruncated(Number(details.earningsPerShare), 2)}
            </dd>
          </div>
          <div className="flex justify-between py-1 align-bottom">
            <dt className="text-soft">
              <Tooltip
                title={<TooltipTitle>{term('gross margin')}</TooltipTitle>}
                light
                openDelay={300}
              >
                <Term>{t('fundamentals.income.details.grossMargin')}</Term>
              </Tooltip>
            </dt>
            <dd className="text-right">
              {toPercentage(Number(details.grossProfitMargin), 2)}
            </dd>
          </div>
          <div className="flex justify-between py-1 align-bottom">
            <dt className="text-soft">
              <Tooltip
                title={<TooltipTitle>{term('profit margins')}</TooltipTitle>}
                light
                openDelay={300}
              >
                <Term>{t('fundamentals.income.details.netProfitMargin')}</Term>
              </Tooltip>
            </dt>
            <dd className="text-right">
              {toPercentage(Number(details.netIncomeMargin), 2)}
            </dd>
          </div>
          <div className="flex justify-between py-1 align-bottom">
            <dt className="text-soft">
              <Tooltip
                title={
                  <TooltipTitle>{term('debt to equity ratio')}</TooltipTitle>
                }
                light
                openDelay={300}
              >
                <Term>{t('fundamentals.income.details.debtEquityRatio')}</Term>
              </Tooltip>
            </dt>
            <dd className="text-right">
              {toPercentage(Number(details.debtToEquityRatio), 1)}
            </dd>
          </div>
        </dl>
        <div>
          <Link href={canonicalURL} className=" text-softer no-underline">
            View full report &rsaquo;
          </Link>
        </div>
      </div>
    </div>
  );
};
